/**
 * @file (De)serialisation : PlayerData <-> enveloppe JSON (Decimal via DECIMAL_PATHS), checksum, migration, completion par valeurs par defaut typees.
 */
import { Decimal, fromBigStr } from '@shared/bignum';
import { hashString } from '../rng/Rng';
import { DECIMAL_PATHS } from '../state/PlayerData';
import type { PlayerData } from '../state/PlayerData';
import { migrateData } from './migrations';
import type { RawData } from './migrations';
import { CURRENT_SAVE_VERSION, SAVE_FORMAT } from './SaveSchema';
import type { SaveEnvelope } from './SaveSchema';

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** PlayerData -> chaine JSON. Decimal.toJSON produit deja une chaine. */
export const serializeSave = (data: PlayerData, nowMs: number): string => {
  data.meta.revision += 1;
  data.meta.lastSavedAtMs = nowMs;
  const json = JSON.stringify(data);
  const envelope: SaveEnvelope = {
    format: SAVE_FORMAT,
    version: CURRENT_SAVE_VERSION,
    savedAtMs: nowMs,
    revision: data.meta.revision,
    checksum: hashString(json).toString(16),
    data: JSON.parse(json) as unknown,
  };
  return JSON.stringify(envelope);
};

export type LoadResult =
  | { ok: true; data: PlayerData; migratedFrom: number }
  | { ok: false; reason: 'empty' | 'corrupt' | 'format' | 'newerVersion' | 'checksum' };

/** Convertit en place les chaines aux chemins DECIMAL_PATHS en Decimal (wildcard `*` = chaque cle d'un objet). */
export const reviveDecimals = (root: Record<string, unknown>, paths: readonly string[] = DECIMAL_PATHS): void => {
  for (const path of paths) {
    const parts = path.split('.');
    const walk = (node: unknown, i: number): void => {
      if (!isObj(node)) return;
      const key = parts[i] as string;
      const last = i === parts.length - 1;
      const keys = key === '*' ? Object.keys(node) : [key];
      for (const k of keys) {
        if (!(k in node)) continue;
        if (last) {
          const v = node[k];
          if (typeof v === 'string' || typeof v === 'number') node[k] = fromBigStr(String(v));
        } else walk(node[k], i + 1);
      }
    };
    walk(root, 0);
  }
};

/**
 * Complete `raw` avec `defaults` : les cles absentes de raw recoivent la valeur par defaut (cas d'un champ ajoute sans migration
 * dediee, ou d'une sauvegarde partiellement corrompue). Les tableaux et valeurs feuilles de raw sont conserves tels quels ;
 * les types incompatibles (objet attendu, primitive trouvee) sont remplaces par la valeur par defaut.
 */
export const fillDefaults = (raw: unknown, defaults: unknown): unknown => {
  if (defaults instanceof Decimal) return raw instanceof Decimal ? raw : defaults;
  if (Array.isArray(defaults)) return Array.isArray(raw) ? raw : defaults;
  if (isObj(defaults)) {
    if (!isObj(raw)) return defaults;
    const out: Record<string, unknown> = { ...raw };
    for (const [k, dv] of Object.entries(defaults)) out[k] = k in raw ? fillDefaults(raw[k], dv) : dv;
    return out;
  }
  if (defaults === null) return raw === undefined ? null : raw;
  return typeof raw === typeof defaults ? raw : defaults;
};

/** Chaine stockee -> PlayerData (migration + revive + defauts). `defaults` = createNewPlayerData(...) frais. */
export const deserializeSave = (json: string | null, defaults: PlayerData, verifyChecksum = true): LoadResult => {
  if (!json) return { ok: false, reason: 'empty' };
  let env: SaveEnvelope;
  try {
    env = JSON.parse(json) as SaveEnvelope;
  } catch {
    return { ok: false, reason: 'corrupt' };
  }
  if (!isObj(env) || env.format !== SAVE_FORMAT || typeof env.version !== 'number' || !isObj(env.data)) return { ok: false, reason: 'format' };
  if (verifyChecksum && hashString(JSON.stringify(env.data)).toString(16) !== env.checksum) return { ok: false, reason: 'checksum' };
  let data: RawData;
  try {
    data = migrateData(env.data as RawData, env.version);
  } catch {
    return { ok: false, reason: 'newerVersion' };
  }
  reviveDecimals(data);
  const filled = fillDefaults(data, defaults) as PlayerData;
  return { ok: true, data: filled, migratedFrom: env.version };
};
